import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import {
  Hub as HubIcon,
  MenuBook as KBIcon,
  TrendingUp as SalesIcon,
  Search as ResearchIcon,
  Mic as MeetingsIcon,
  Assignment as DeliveryIcon,
  SmartToy as AgentIcon,
  PlayArrow,
  Mail,
  Archive as ArchiveIcon,
  ExpandMore,
  ExpandLess,
  OpenInNew,
} from "@mui/icons-material";
import { useAgents } from "@/hooks/useAgents";
import apiClient from "@/api/client";
import type { Agent } from "@/types";
import { brand } from "@/theme";

const AGENT_ICONS: Record<string, React.ReactNode> = {
  "AI-00": <HubIcon />,
  "AI-01": <KBIcon />,
  "AI-02": <SalesIcon />,
  "AI-03": <ResearchIcon />,
  "AI-04": <MeetingsIcon />,
  "AI-05": <DeliveryIcon />,
};

const AGENT_COLORS: Record<string, string> = {
  "AI-00": brand.forestGreen,
  "AI-01": "#2E7D32",
  "AI-02": "#C4922A",
  "AI-03": "#1565C0",
  "AI-04": "#6A1B9A",
  "AI-05": "#E65100",
};

interface AgentMessage {
  id: string;
  from_agent: string;
  to_agent: string;
  type: string;
  priority: string;
  subject: string;
  body: string;
  status: string;
  created_at: string;
  reviewed_at: string | null;
  context_refs: Record<string, unknown> | null;
}

interface MessageResponse {
  messages: AgentMessage[];
  total: number;
  counts: Record<string, number>;
}

function AgentCard({ agent, onHeartbeat }: { agent: Agent; onHeartbeat?: () => void }) {
  const navigate = useNavigate();
  const color = AGENT_COLORS[agent.code] ?? brand.forestGreen;

  return (
    <Card
      sx={{
        height: "100%",
        borderLeft: `4px solid ${color}`,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 4 },
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/agents/${agent.id}`)}
        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
              {AGENT_ICONS[agent.code] ?? <AgentIcon />}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {agent.code}
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {agent.name}
              </Typography>
            </Box>
            {onHeartbeat && (
              <Tooltip title="Run heartbeat now">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onHeartbeat();
                  }}
                  sx={{ color }}
                >
                  <PlayArrow fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: "italic" }}>
            {agent.tagline}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            <Chip label={agent.model_id} size="small" variant="outlined" />
            {agent.heartbeat_cron && (
              <Chip label={formatCron(agent.heartbeat_cron)} size="small" variant="outlined" />
            )}
          </Box>

          {agent.goals && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Key Goals
              </Typography>
              {agent.goals.slice(0, 2).map((goal, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: "0.8rem", mt: 0.25 }}>
                  - {goal}
                </Typography>
              ))}
              {agent.goals.length > 2 && (
                <Typography variant="caption" color="text.secondary">
                  +{agent.goals.length - 2} more
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function formatCron(cron: string): string {
  const parts = cron.split(" ");
  const hour = parts[1];
  const dow = parts[4];
  const dowMap: Record<string, string> = {
    "1-5": "Weekdays",
    "1,4": "Mon & Thu",
    "1": "Mondays",
    "*": "Daily",
  };
  return `${hour}:00 ${dowMap[dow ?? ""] ?? dow}`;
}

const PRIORITY_COLORS: Record<string, "default" | "warning" | "error" | "info"> = {
  low: "info",
  normal: "default",
  high: "warning",
  urgent: "error",
};

function MessageDetail({ context }: { context: Record<string, unknown> }) {
  const navigate = useNavigate();

  // Render different details based on what's in the context
  const items: Array<{ label: string; value: string; action?: () => void }> = [];

  // Cold leads
  const coldLeads = context.cold_leads as
    | { count?: number; leads?: Array<{ id: string; name: string; last_contact: string }> }
    | undefined;
  if (coldLeads?.leads?.length) {
    for (const lead of coldLeads.leads) {
      items.push({
        label: `Cold lead: ${lead.name}`,
        value: `Last contact: ${lead.last_contact}`,
        action: () => navigate(`/crm/companies/${lead.id}`),
      });
    }
  }

  // Overdue follow-ups
  const overdue = context.overdue_followups as
    | {
        count?: number;
        followups?: Array<{ description: string; company: string; days_overdue: number }>;
      }
    | undefined;
  if (overdue?.followups?.length) {
    for (const f of overdue.followups) {
      items.push({
        label: `Overdue: ${f.description}`,
        value: `${f.company ?? "No company"} · ${f.days_overdue} day(s) overdue`,
      });
    }
  }

  // Stale articles
  const stale = context.stale_articles as
    | { count?: number; articles?: Array<{ id: string; title: string }> }
    | undefined;
  if (stale?.articles?.length) {
    for (const a of stale.articles) {
      items.push({
        label: `Stale article: ${a.title}`,
        value: "Not updated in 90+ days",
        action: () => navigate(`/kb/${a.id}/edit`),
      });
    }
  }

  // Duplicates
  const dupes = context.potential_duplicates as
    | { pairs?: Array<{ article_a: string; article_b: string; similarity: number }> }
    | undefined;
  if (dupes?.pairs?.length) {
    for (const d of dupes.pairs) {
      items.push({
        label: `Potential duplicate`,
        value: `"${d.article_a}" ↔ "${d.article_b}" (${Math.round(d.similarity * 100)}% similar)`,
      });
    }
  }

  // Embeddings
  const embeddings = context.embeddings as { backfilled?: number; failed?: number } | undefined;
  if (embeddings?.backfilled) {
    items.push({ label: "Embeddings backfilled", value: `${embeddings.backfilled} article(s)` });
  }

  // Pipeline
  const pipeline = context.pipeline as
    | { stages?: Record<string, number>; total_companies?: number }
    | undefined;
  if (pipeline?.stages) {
    const active = Object.entries(pipeline.stages)
      .filter(([s]) => !["won", "lost", "dormant"].includes(s))
      .reduce((sum, [, c]) => sum + c, 0);
    items.push({
      label: "Active pipeline",
      value: `${active} companies in active stages`,
      action: () => navigate("/crm"),
    });
  }

  // Suggested topics
  const topics = context.suggested_topics as { suggested_topics?: string[] } | undefined;
  if (topics?.suggested_topics?.length) {
    for (const t of topics.suggested_topics) {
      items.push({ label: "Research suggestion", value: t });
    }
  }

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 5 }}>
        No actionable details in this report.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1, pl: 5, display: "flex", flexDirection: "column", gap: 0.75 }}>
      {items.map((item, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            cursor: item.action ? "pointer" : "default",
            "&:hover": item.action ? { borderColor: "primary.main", boxShadow: 1 } : {},
          }}
          onClick={item.action}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {item.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.value}
            </Typography>
          </Box>
          {item.action && <OpenInNew fontSize="small" color="action" />}
        </Box>
      ))}
    </Box>
  );
}

function MessageFeed() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<MessageResponse>({
    queryKey: ["agent-messages", statusFilter],
    queryFn: async () => {
      const params =
        statusFilter === "active"
          ? "limit=20"
          : statusFilter === "all"
            ? "limit=50"
            : `status=${statusFilter}&limit=20`;
      const res = await apiClient.get<MessageResponse>(`/messages?${params}`);
      return res.data;
    },
    refetchInterval: 30000,
  });

  const archiveMutation = useMutation({
    mutationFn: async (action: string) => {
      if (action.startsWith("single:")) {
        await apiClient.patch(`/messages/${action.replace("single:", "")}/archive`);
      } else if (action === "reviewed") {
        await apiClient.post("/messages/archive-reviewed");
      } else if (action === "all") {
        await apiClient.post("/messages/archive-all");
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-messages"] }),
  });

  const messages = data?.messages ?? [];
  const counts = data?.counts ?? {};
  const activeCount = (counts["sent"] ?? 0) + (counts["reviewed"] ?? 0);
  const filteredMessages =
    statusFilter === "active" ? messages.filter((m) => m.status !== "archived") : messages;

  if (isLoading) return <CircularProgress size={20} />;

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        {["active", "sent", "reviewed", "archived", "all"].map((s) => (
          <Chip
            key={s}
            label={`${s}${counts[s] != null ? ` (${counts[s]})` : s === "active" ? ` (${activeCount})` : ""}`}
            size="small"
            variant={statusFilter === s ? "filled" : "outlined"}
            color={statusFilter === s ? "primary" : "default"}
            onClick={() => setStatusFilter(s)}
            sx={{ textTransform: "capitalize" }}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        {activeCount > 0 && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => archiveMutation.mutate("reviewed")}
              disabled={archiveMutation.isPending || !counts["reviewed"]}
            >
              Archive Reviewed
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => archiveMutation.mutate("all")}
              disabled={archiveMutation.isPending}
            >
              Archive All
            </Button>
          </>
        )}
      </Box>

      {filteredMessages.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {statusFilter === "active"
            ? "No active messages. Run a heartbeat to generate reports."
            : `No ${statusFilter} messages.`}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {filteredMessages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              borderRadius: 1,
              bgcolor: msg.status === "archived" ? "#f5f5f5" : "#fafafa",
              opacity: msg.status === "archived" ? 0.6 : 1,
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 1.5 }}>
              <Avatar
                sx={{
                  bgcolor: AGENT_COLORS[msg.from_agent] ?? brand.forestGreen,
                  width: 28,
                  height: 28,
                  fontSize: "0.7rem",
                }}
              >
                {AGENT_ICONS[msg.from_agent] ?? <AgentIcon sx={{ fontSize: 14 }} />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {msg.from_agent} → {msg.to_agent}
                  </Typography>
                  <Chip
                    label={msg.type}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                  />
                  <Chip
                    label={msg.priority}
                    size="small"
                    color={PRIORITY_COLORS[msg.priority]}
                    sx={{ height: 18, fontSize: "0.65rem" }}
                  />
                  <Chip
                    label={msg.status}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                    color={
                      msg.status === "sent"
                        ? "info"
                        : msg.status === "reviewed"
                          ? "success"
                          : "default"
                    }
                  />
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                  {msg.subject}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  {msg.body}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(msg.created_at).toLocaleString()}
                </Typography>
              </Box>
              {msg.context_refs && (
                <IconButton
                  size="small"
                  title={expandedId === msg.id ? "Collapse" : "View details"}
                  onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                >
                  {expandedId === msg.id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              )}
              {msg.status !== "archived" && (
                <IconButton
                  size="small"
                  title="Archive"
                  onClick={() => archiveMutation.mutate(`single:${msg.id}`)}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {expandedId === msg.id && msg.context_refs && (
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <MessageDetail context={msg.context_refs} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function AgentDashboard() {
  const { data: agents, isLoading, error } = useAgents();
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning",
  });

  const heartbeatMutation = useMutation({
    mutationFn: async (agentCode: string) => {
      const res = await apiClient.post(`/heartbeat/${agentCode}`);
      return res.data;
    },
    onSuccess: (data, agentCode) => {
      queryClient.invalidateQueries({ queryKey: ["agent-messages"] });
      const status = data.status === "healthy" ? "healthy" : "needs attention";
      setSnackbar({
        open: true,
        message: `${agentCode} heartbeat complete — ${status}`,
        severity: data.status === "healthy" ? "success" : "warning",
      });
    },
    onError: (_, agentCode) => {
      setSnackbar({ open: true, message: `${agentCode} heartbeat failed`, severity: "error" });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load agents. Is the backend running?</Alert>;
  }

  if (!agents || agents.length === 0) {
    return <Alert severity="info">No agents found. Seed them via POST /api/v1/seed/agents.</Alert>;
  }

  const chiefOfStaff = agents.find((a) => a.code === "AI-00");
  const subordinates = agents.filter((a) => a.code !== "AI-00");

  // Only AI-01 has a heartbeat implemented for now
  const heartbeatAgents = new Set(["AI-00", "AI-01", "AI-02", "AI-03"]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Agent Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        The Mastermind agent ecosystem. All agents report to the Chief of Staff.
      </Typography>

      {/* Chief of Staff — full width at top */}
      {chiefOfStaff && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Orchestrator
          </Typography>
          <AgentCard
            agent={chiefOfStaff}
            onHeartbeat={() => heartbeatMutation.mutate(chiefOfStaff.code)}
          />
        </Box>
      )}

      {/* Connection line */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <Box sx={{ width: 2, height: 32, bgcolor: "divider" }} />
      </Box>

      {/* Subordinate agents — grid */}
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Specialist Agents
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
          gap: 2,
          mb: 4,
        }}
      >
        {subordinates.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onHeartbeat={
              heartbeatAgents.has(agent.code)
                ? () => heartbeatMutation.mutate(agent.code)
                : undefined
            }
          />
        ))}
      </Box>

      {/* Agent comms feed */}
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Mail color="action" />
        <Typography variant="h5">Agent Communications</Typography>
      </Box>
      <MessageFeed />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
